/**
 * Modal oyna — video yo'riqnoma, galereya ko'rish oynasi va tasdiqlashlar uchun.
 *
 * WCAG 2.1: `role="dialog"` + `aria-modal`, ochilganda fokus oyna ichiga
 * ko'chadi va Tab tugmasi oyna ichida aylanadi, `Esc` yopadi, yopilganda fokus
 * oynani ochgan elementga qaytadi. Orqa fon sahifasi aylantirilmaydi.
 */
import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

import { useI18n } from "@/shared/i18n";
import { Icon } from "@/shared/ui/Icon";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, video[controls], [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  title,
  onClose,
  size = "md",
  className = "",
  children,
  footer,
  onKeyDown,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Qo'shimcha klaviatura boshqaruvi (masalan galereyada strelkalar). */
  onKeyDown?: (event: KeyboardEvent) => void;
}) {
  const { t } = useI18n();
  const panel = useRef<HTMLDivElement>(null);
  const keyHandler = useRef(onKeyDown);
  keyHandler.current = onKeyDown;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    const focusFirst = () => {
      const first = panel.current?.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panel.current)?.focus();
    };
    focusFirst();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "Tab" && panel.current) {
        const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      keyHandler.current?.(event);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-root">
      <button
        type="button"
        className="modal-scrim"
        aria-label={t("action.close")}
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        className={`modal is-${size} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panel}
      >
        <header className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t("action.close")}>
            <Icon name="close" size={18} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer ? <footer className="modal-foot">{footer}</footer> : null}
      </div>
    </div>
  );
}
